import { Component, computed, input, OnInit, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdvancedEquipmentConditions, GearHighlightConfig, WeaponFilter } from '../../../../models/filter-models';
import { BaseTypeTier, GearBaseTypeMode, Rarity, WeaponType } from '../../../../models/enums';
import { FilterService } from '../../../../services/filter.service';
import { AutocompleteComponent } from '../../../../common/autocomplete/autocomplete.component';
import { BaseType } from '../../../../models/item-data-models';
import { SelectGearTierComponent } from '../../../../common/select/select-gear-tier/select-gear-tier.component';
import { SelectComponent, SelectOptionComponent } from '../../../../common/select/select.component';
import { BaseComponent } from '../../../../common/base.component';
import { ItemDataService } from '../../../../services/item-data.service';
import { GenerationHelperService } from '../../../../services/generators/generation-helper.service';
import { AdvancedEquipmentConditionsComponent } from '../../advanced-equipment-conditions/advanced-equipment-conditions.component';
import { MyEquipmentHighlightsComponent } from '../../my-equipment-highlights/my-equipment-highlights.component';
import { SelectRarityComponent } from '../../../../common/select/select-rarity/select-rarity.component';
import { TooltipWrapperComponent } from "../../../../common/tooltip-wrapper/tooltip-wrapper.component";

@Component({
  selector: 'app-weapon-filter',
  imports: [FormsModule, AutocompleteComponent, SelectGearTierComponent, SelectComponent, SelectOptionComponent,
    SelectRarityComponent, AdvancedEquipmentConditionsComponent, MyEquipmentHighlightsComponent, TooltipWrapperComponent],
  templateUrl: './weapon-filter.component.html',
  styleUrl: './weapon-filter.component.scss'
})
export class WeaponFilterComponent extends BaseComponent implements OnInit {
  weapon = input.required<WeaponFilter>();
  index = input.required<number>();
  remove = output<number>();

  GearBaseTypeMode = GearBaseTypeMode;

  hasAdvancedConditions = 0;
  advancedConditionsOpen = false;
  hasHighlightConfig = 0;
  highlightConfigOpen = false;

  weaponTypes: BaseType[] = [];

  specificBaseTypeAutocompleteOptions = computed(() => {
    const w = this.weapon();
    return this.generationHelper.getMyGearBaseTypeAutocompleteOptions(w.weaponType, undefined, undefined);
  });

  constructor(private filterService: FilterService, private itemDataService: ItemDataService, private generationHelper: GenerationHelperService) {
    super();
    this.weaponTypes = (this.itemDataService.getAllWeaponTypes(false) as WeaponType[]).map(w => {
      return { name: w, image: this.itemDataService.getItemData().find(i => i.itemClass === w)!.baseTypes[0].image! };
    });
  }

  ngOnInit(): void {
    this.hasAdvancedConditions = this.getNumberOfAdvancedConditions();
    this.hasHighlightConfig = this.getNumberOfHighlightConfig();
  }

  toggleAdvancedOptionsOpen() {
    this.advancedConditionsOpen = !this.advancedConditionsOpen;
  }

  toggleHighlightConfigOpen() {
    this.highlightConfigOpen = !this.highlightConfigOpen;
  }

  getNumberOfAdvancedConditions() {
    return AdvancedEquipmentConditions.hasChanges(this.weapon());
  }

  getNumberOfHighlightConfig() {
    return GearHighlightConfig.hasChanges(this.weapon().highlightConfig);
  }

  toggleShowWeapon() {
    this.weapon().show = !this.weapon().show;
    if (!this.weapon().show) {
      this.advancedConditionsOpen = false;
      this.highlightConfigOpen = false;
    }
    this.updateFilter();
  }

  removeWeaponType() {
    this.remove.emit(this.index());
  }

  weaponTypeChanged(weaponTypes: string[]) {
    this.weapon().weaponType = weaponTypes.map(w => w as WeaponType);
    if (this.weapon().baseTypeMode === GearBaseTypeMode.Specific) {
      this.pruneWeaponSpecificBaseTypes();
    }
    this.updateFilter();
  }

  toggleWeaponBaseTypeMode(): void {
    if (this.weapon().baseTypeMode === GearBaseTypeMode.Tier) {
      this.weapon().baseTypeMode = GearBaseTypeMode.Specific;
      if (this.weapon().specificBaseTypes.length === 0) {
        this.weapon().specificBaseTypes = ['All'];
      }
    } else {
      this.weapon().baseTypeMode = GearBaseTypeMode.Tier;
    }
    this.updateFilter();
  }

  specificWeaponBaseTypesChanged(values: string[]): void {
    this.weapon().specificBaseTypes = values;
    this.updateFilter();
  }

  private pruneWeaponSpecificBaseTypes(): void {
    const weapon = this.weapon();
    if (weapon.specificBaseTypes.includes('All')) {
      return;
    }
    const eligible = new Set(this.generationHelper.resolveGearRuleEligibleBaseTypeNames(weapon.weaponType, undefined, undefined));
    const next = weapon.specificBaseTypes.filter(b => eligible.has(b));
    weapon.specificBaseTypes = next.length > 0 ? next : ['All'];
  }

  minUnidentifiedTierChanged(unidentifiedTier: number | null) {
    const weapon = this.weapon();
    if (weapon.rarity === Rarity.Normal && unidentifiedTier) {
      weapon.rarity = Rarity.Magic;
    }

    if (weapon.highlightConfig?.rareMinTier && unidentifiedTier) {
      weapon.highlightConfig.rareMinTier = unidentifiedTier;
    }
    if (weapon.highlightConfig?.magicMinTier && unidentifiedTier) {
      weapon.highlightConfig.magicMinTier = unidentifiedTier;
    }
    if (weapon.highlightConfig?.lowTierRareMinTier && unidentifiedTier) {
      weapon.highlightConfig.lowTierRareMinTier = unidentifiedTier;
    }
    if (weapon.highlightConfig?.lowTierMagicMinTier && unidentifiedTier) {
      weapon.highlightConfig.lowTierMagicMinTier = unidentifiedTier;
    }

    this.updateFilter();
  }

  minRarityChanged(rarity: Rarity) {
    const weapon = this.weapon();
    if (rarity === Rarity.Normal) {
      weapon.unidentifiedTier = undefined;
      weapon.highlightConfig.regularNormal = true;
      weapon.highlightConfig.regularMagic = true;
      weapon.highlightConfig.magic = true;
      weapon.highlightConfig.lowTierMagic = true;
    }

    if (weapon.rarity === Rarity.Magic) {
      weapon.highlightConfig.regularNormal = false;
      weapon.highlightConfig.regularMagic = true;
      weapon.highlightConfig.magic = true;
      weapon.highlightConfig.lowTierMagic = true;
    }

    if (weapon.rarity === Rarity.Rare) {
      weapon.highlightConfig.regularNormal = false;
      weapon.highlightConfig.regularMagic = false;
      weapon.highlightConfig.magic = false;
      weapon.highlightConfig.lowTierMagic = false;
    }

    this.hasHighlightConfig = this.getNumberOfHighlightConfig();

    this.updateFilter();
  }

  minBaseTypeTierChanged(baseTypeTier: any) {
    const weapon = this.weapon();
    if (baseTypeTier === BaseTypeTier.Basic) {
      weapon.highlightConfig.lowTierMagic = false;
      weapon.highlightConfig.lowTierRare = false;
    } else {
      weapon.highlightConfig.lowTierMagic = weapon.rarity === Rarity.Normal || weapon.rarity === Rarity.Magic ? true : false;
      weapon.highlightConfig.lowTierRare = true;
    }

    this.hasHighlightConfig = this.getNumberOfHighlightConfig();

    this.updateFilter();
  }

  updateFilter() {
    this.filterService.updateFilter();
  }
}
